import React, {useMemo, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {message} from "antd";

import css from './OrderDetailsSingleDateDishes.module.css';
import OrderDetailsDateDish from "../OrderDetailsDateDish/OrderDetailsDateDish";
import {
    OrdersMoveDeliveryToAnotherDateModal
} from "../OrdersMoveDeliveryToAnotherDateModal/OrdersMoveDeliveryToAnotherDateModal";
import {OrdersReplaceDishOrderModal} from "../OrdersReplaceDishOrderModal/OrdersReplaceDishOrderModal";
import {OrdersInfoSingleDishModal} from "../OrdersInfoSingleDishModal/OrdersInfoSingleDishModal";
import {DishType} from "../../../../constants/enums";
import {ordersService} from "../../../../services/orders-services";
import {ordersActions} from "../../../../store/slices/orders-slices";


const OrderDetailsSingleDateDishes = ({obj, order, setIsShowCancellation}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [isShowMoveDelivery, setIsShowMoveDelivery] = useState(false);
    const [isShowReplace, setIsShowReplace] = useState(false);

    const {date, dishes} = obj;

    const [idForUpdateDishInOrderClient, setIdForUpdateDishInOrderClient] = useState();
    const [currentDishForReplacement, setCurrentDishForReplacement] = useState(null);
    const [replacementType, setReplacementType] = useState('base');
    const [selectedDishIdForInfo, setSelectedDishIdForInfo] = useState(null);

    const isWeekend = useMemo(() => {
        const parsedDate = new Date(date);
        const day = parsedDate.getDay();
        return day === 0 || day === 6; // неділя або субота
    }, [date]);

    // Determine order status
    const isActiveOrder = useMemo(() => {
        return order?.status === 'active' || order?.status === 'confirmed';
    }, [order]);

    const isBeforeOrder = useMemo(() => {
        return order?.status === 'pending' || order?.status === 'draft' || !order?.status;
    }, [order]);

    // Build a set of orders_dishes IDs that have at least one main-dish replacement in history
    const replacedDishIds = useMemo(() => {
        const replacements = order?.dishReplacements;
        if (!replacements || replacements.length === 0) return new Set();
        return new Set(replacements.map(r => r.orderDishId));
    }, [order]);

    // Build sets of dish IDs (dishes table) that are the result of a protein/sauce replacement
    const proteinReplacedNewIds = useMemo(() => {
        const logs = order?.orderLogs;
        if (!logs || logs.length === 0) return new Set();
        return new Set(
            logs
                .filter(log => log.action === 'PROTEIN_REPLACED')
                .map(log => log.metadata?.newDishId)
                .filter(Boolean)
        );
    }, [order]);

    const sauceReplacedNewIds = useMemo(() => {
        const logs = order?.orderLogs;
        if (!logs || logs.length === 0) return new Set();
        return new Set(
            logs
                .filter(log => log.action === 'SAUCE_REPLACED')
                .map(log => log.metadata?.newDishId)
                .filter(Boolean)
        );
    }, [order]);

    // Calculate total nutritional values for the day
    const nutritionalSummary = useMemo(() => {
        if (!dishes || dishes.length === 0) {
            return {
                totalEnergy: 0,
                totalProteins: 0,
                totalFats: 0,
                totalCarbs: 0,
                totalWeight: 0,
                proteinPercentage: 0,
                fatPercentage: 0,
                carbPercentage: 0
            };
        }

        const addDishValues = (acc, d) => {
            if (!d) return acc;
            return {
                totalEnergy: acc.totalEnergy + (Number(d.energy) || 0),
                totalProteins: acc.totalProteins + (Number(d.proteins) || 0),
                totalFats: acc.totalFats + (Number(d.fats) || 0),
                totalCarbs: acc.totalCarbs + (Number(d.carbohydrates) || Number(d.carbs) || 0),
                totalWeight: acc.totalWeight + (Number(d.weightNetto) || Number(d.weight) || 0)
            };
        };

        const totals = dishes.reduce((acc, dishObj) => {
            let result = addDishValues(acc, dishObj.dish);
            if (dishObj.type === DishType.BASE || dishObj.type === 'BASE') {
                result = addDishValues(result, dishObj.proteinDish);
                result = addDishValues(result, dishObj.sauceDish);
            }
            return result;
        }, {
            totalEnergy: 0,
            totalProteins: 0,
            totalFats: 0,
            totalCarbs: 0,
            totalWeight: 0
        });

        // Calculate calories from macronutrients (proteins: 4 kcal/g, fats: 9 kcal/g, carbs: 4 kcal/g)
        const proteinCalories = totals.totalProteins * 4;
        const fatCalories = totals.totalFats * 9;
        const carbCalories = totals.totalCarbs * 4;
        const totalMacroCalories = proteinCalories + fatCalories + carbCalories;

        // Calculate percentages based on macronutrient calories
        const proteinPercentage = totalMacroCalories > 0 ? (proteinCalories / totalMacroCalories) * 100 : 0;
        const fatPercentage = totalMacroCalories > 0 ? (fatCalories / totalMacroCalories) * 100 : 0;
        const carbPercentage = totalMacroCalories > 0 ? (carbCalories / totalMacroCalories) * 100 : 0;

        return {
            ...totals,
            proteinPercentage,
            fatPercentage,
            carbPercentage
        };
    }, [dishes]);

    // Calculate total cost for the day
    const dailyCostTotal = useMemo(() => {
        if (!dishes || dishes.length === 0) return 0;
        return dishes.reduce((total, dishObj) => {
            let cost = Number(dishObj.dish?.cost) || 0;
            if (dishObj.type === DishType.BASE || dishObj.type === 'BASE') {
                cost += Number(dishObj.proteinDish?.cost) || 0;
                cost += Number(dishObj.sauceDish?.cost) || 0;
            }
            return total + cost;
        }, 0);
    }, [dishes]);

    // Simple pie chart component
    const MacronutrientPieChart = ({ proteinPercentage, fatPercentage, carbPercentage }) => {
        const size = 40;
        const strokeWidth = 4;
        const radius = (size - strokeWidth) / 2;
        const circumference = radius * 2 * Math.PI;
        
        // Calculate stroke dash arrays for each segment
        const proteinDash = (proteinPercentage / 100) * circumference;
        const fatDash = (fatPercentage / 100) * circumference;
        const carbDash = (carbPercentage / 100) * circumference;
        
        // Calculate rotation offsets
        const proteinOffset = 0;
        const fatOffset = proteinDash;
        const carbOffset = proteinDash + fatDash;

        return (
            <div className={css.pieChartContainer}>
                <svg width={size} height={size} className={css.pieChart}>
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="transparent"
                        stroke="#e0e0e0"
                        strokeWidth={strokeWidth}
                    />
                    {/* Proteins - Blue */}
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="transparent"
                        stroke="#3498db"
                        strokeWidth={strokeWidth}
                        strokeDasharray={`${proteinDash} ${circumference}`}
                        strokeDashoffset={-proteinOffset}
                        transform={`rotate(-90 ${size / 2} ${size / 2})`}
                        className={css.pieSegment}
                    />
                    {/* Fats - Orange */}
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="transparent"
                        stroke="#f39c12"
                        strokeWidth={strokeWidth}
                        strokeDasharray={`${fatDash} ${circumference}`}
                        strokeDashoffset={-fatOffset}
                        transform={`rotate(-90 ${size / 2} ${size / 2})`}
                        className={css.pieSegment}
                    />
                    {/* Carbohydrates - Green */}
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="transparent"
                        stroke="#27ae60"
                        strokeWidth={strokeWidth}
                        strokeDasharray={`${carbDash} ${circumference}`}
                        strokeDashoffset={-carbOffset}
                        transform={`rotate(-90 ${size / 2} ${size / 2})`}
                        className={css.pieSegment}
                    />
                </svg>
                <div className={css.pieChartLegend}>
                    <div className={css.legendItem}>
                        <div className={css.legendColor} style={{backgroundColor: '#3498db'}}></div>
                        <div className={css.legendTextContainer}>
                            <span className={css.legendText}>P</span>
                            <span className={css.legendPercentage}>{Math.round(proteinPercentage)}%</span>
                        </div>
                    </div>
                    <div className={css.legendItem}>
                        <div className={css.legendColor} style={{backgroundColor: '#f39c12'}}></div>
                        <div className={css.legendTextContainer}>
                            <span className={css.legendText}>F</span>
                            <span className={css.legendPercentage}>{Math.round(fatPercentage)}%</span>
                        </div>
                    </div>
                    <div className={css.legendItem}>
                        <div className={css.legendColor} style={{backgroundColor: '#27ae60'}}></div>
                        <div className={css.legendTextContainer}>
                            <span className={css.legendText}>C</span>
                            <span className={css.legendPercentage}>{Math.round(carbPercentage)}%</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const handleMoveClick = () => {
        setIsShowMoveDelivery(true);
    }

    // Enhanced dish replacement handler
    const handleDishReplacement = (dishId, dishObj, type = 'base') => {
        setIdForUpdateDishInOrderClient(dishId);
        setCurrentDishForReplacement(dishObj);
        setReplacementType(type);
        setIsShowReplace(true);
    };

    const handleRemoveSubDish = async (dishId, kind) => {
        try {
            const updateData = kind === 'protein' ? { proteinDishId: null } : { sauceDishId: null };
            await ordersService.updateDishById(dishId, updateData);
            dispatch(ordersActions.changeTrigger());
            message.success(t('sub-dish-removed'));
        } catch {
            message.error(t('error'));
        }
    };

    return (
        <div className={css.wrap}>
            <div className={css.header}>
                <span className={`${css.currentDate} ${isWeekend ? css.weekend : ''}`}>
                    {date || '-'}
                </span>

                <span className={css.move} onClick={handleMoveClick}>{t('move')}</span>
            </div>

            {/* Nutritional Summary Section */}
            <div className={css.nutritionalSummary}>
                <div className={css.summaryTitle}>{t('daily-total')}</div>
                <div className={css.summaryContent}>
                    <div className={css.summaryItem}>
                        <span className={css.summaryLabel}>{t('Weight')}:</span>
                        <span className={css.summaryValue}>{Math.round(nutritionalSummary.totalWeight)} g</span>
                    </div>
                    <div className={css.summaryItem}>
                        <span className={css.summaryLabel}>{t('modal-energy')}:</span>
                        <span className={css.summaryValue}>{Math.round(nutritionalSummary.totalEnergy)} kcal</span>
                    </div>
                    <div className={css.summaryItem}>
                        <span className={css.summaryLabel}>{t('modal-proteins')}:</span>
                        <span className={css.summaryValue}>
                            {Math.round(nutritionalSummary.totalProteins)} g 
                            <span className={css.percentage}>({Math.round(nutritionalSummary.proteinPercentage)}%)</span>
                        </span>
                    </div>
                    <div className={css.summaryItem}>
                        <span className={css.summaryLabel}>{t('modal-fats')}:</span>
                        <span className={css.summaryValue}>
                            {Math.round(nutritionalSummary.totalFats)} g 
                            <span className={css.percentage}>({Math.round(nutritionalSummary.fatPercentage)}%)</span>
                        </span>
                    </div>
                    <div className={css.summaryItem}>
                        <span className={css.summaryLabel}>{t('modal-carbohydrates')}:</span>
                        <span className={css.summaryValue}>
                            {Math.round(nutritionalSummary.totalCarbs)} g 
                            <span className={css.percentage}>({Math.round(nutritionalSummary.carbPercentage)}%)</span>
                        </span>
                    </div>
                    <div className={css.summaryItem}>
                        <span className={css.summaryLabel}>{t('Cost')}:</span>
                        <span className={css.summaryValue}>{dailyCostTotal.toFixed(2)}</span>
                    </div>
                    <div className={css.summaryItem}>
                        <MacronutrientPieChart
                            proteinPercentage={nutritionalSummary.proteinPercentage}
                            fatPercentage={nutritionalSummary.fatPercentage}
                            carbPercentage={nutritionalSummary.carbPercentage}
                        />
                    </div>
                </div>
            </div>

            <div className={css.dishesBlock}>
                {dishes && dishes
                    .map(dish => <OrderDetailsDateDish
                        key={dish.Fid || dish.id}
                        obj={dish}
                        isReplaced={replacedDishIds.has(dish.Fid || dish.id)}
                        isProteinReplaced={proteinReplacedNewIds.has(dish.proteinDish?.id)}
                        isSauceReplaced={sauceReplacedNewIds.has(dish.sauceDish?.id)}
                        onReplaceClick={(type) => handleDishReplacement(dish.Fid || dish.id, dish, type)}
                        onRemoveSubDish={(kind) => handleRemoveSubDish(dish.Fid || dish.id, kind)}
                        onDishClick={(dishId) => setSelectedDishIdForInfo(dishId)}
                    />)}
            </div>

            {isShowMoveDelivery && <OrdersMoveDeliveryToAnotherDateModal
                setIsShowMoveDelivery={setIsShowMoveDelivery}
                date={date}
                order={order}
                setIsShowCancellation={setIsShowCancellation}
            />}

            {isShowReplace && <OrdersReplaceDishOrderModal
                setIsShowReplace={setIsShowReplace}
                date={date}
                idForUpdateDishInOrderClient={idForUpdateDishInOrderClient}
                currentDish={currentDishForReplacement}
                replacementType={replacementType}
                isActiveOrder={isActiveOrder}
                isBeforeOrder={isBeforeOrder}
            />}

            {selectedDishIdForInfo && (
                <OrdersInfoSingleDishModal
                    setIsShowSingleDish={() => setSelectedDishIdForInfo(null)}
                    selectedDishId={selectedDishIdForInfo}
                />
            )}
        </div>
    );
};

export {OrderDetailsSingleDateDishes as OrderInfoClientSingleDateDishes};